// "UNOHDETUT KÄYTTÄJÄT" — the decisions behind the operator's permanent deletion of a forgotten person's
// row (owner's request 4.11.2026, task 77dd58bf).
//
// WHY THIS IS A MODULE AND NOT A `switch` INSIDE THE COMPONENT, which is the arrangement
// `tenantDeletion.ts` and `tenantRoleHolders.ts` already use in this directory. Every function below picks
// an i18n KEY from a WIRE VALUE, and almost none of those values is reachable by hand: four refusal codes,
// eight retained-record kinds, and the one state where the row is visible and the button must NOT be
// offered (an unfinished erasure). Under `fallbackLng: "fi"` a wrong or missing key on any of those paths
// ships silently — which is what `src/i18n/platform-i18n.test.mjs` reads this file's literals to prevent,
// and what `forgotten-users.test.mjs` exercises at states a browser run cannot easily be put into
// (ADR-0002 Layer 2 entry rule A).
//
// IT IMPORTS NOTHING AT RUNTIME, deliberately: that is what lets Node's own test runner load it under
// `--experimental-strip-types`, like its neighbours.

/** The problem-details shape these readers need, declared structurally for the reason in the header. */
export type PurgeProblem = { [key: string]: unknown } | undefined;

/**
 * One retained class, as the list and the refusal both carry it (`RetainedRecord`'s wire shape).
 *
 * `rows` is `number | string` because that is what the generator renders an `int32` as — the same union
 * `Outstanding.count` carries in `tenantDeletion.ts`, handled the same way: accepted when it reads as a
 * finite number, and interpolated as it arrived.
 */
export type Retained = {
  kind: string;
  rows: number | string;
  retainedUntil?: string | null;
  ground?: string | null;
};

/**
 * Problem `error` codes (`PlatformTenantEndpoints.ForgottenUserPurgeErrors`), spelled here a SECOND time
 * rather than imported from the API client, for the zero-runtime-import reason in the header.
 */
export const ERROR_RETAINED_DATA_PRESENT = "retained_data_present";
export const ERROR_ERASURE_INCOMPLETE = "erasure_incomplete";
export const ERROR_USER_NOT_FORGOTTEN = "user_not_forgotten";
export const ERROR_REFUSED_BY_DATABASE = "refused_by_database";

/**
 * The kind whose line is NOT a retention sentence (`RetainedRecordKinds.SharedIdentity`). The erasure
 * deliberately left the person's realm identity standing because another tenant shares the subject
 * (ADR-0018 §5 point 3), so the shredded row is the only thing denying that account a fresh mirror in this
 * tenant — a fact about authentication, with no row count and no clock.
 */
export const KIND_SHARED_IDENTITY = "shared_identity";

/**
 * The retained-record kinds (`RetainedRecordKinds`), spelled here for the same reason.
 *
 * The first four are ADR-0020 §10's own class names — the backend takes them from
 * `HrmRetentionClasses` rather than spelling them beside it, so these four strings are that ADR's
 * vocabulary and not this feature's. The last four are the product's: the training record, the
 * certificates that hang off it, a person link, and the shared realm identity.
 */
export const KINDS = [
  "employment_core",
  "identity_number",
  "work_permit",
  "absence_record",
  "training_record",
  "certificate",
  "person_link",
  KIND_SHARED_IDENTITY,
] as const;

/**
 * What a row's own action offers.
 *
 *   * `purge` — the server says `purgeable`, so the button is live.
 *   * `retained` — Verso's own data holds the row. NOT a disabled button with no explanation: the classes
 *     are listed beside it, because the only thing an operator can do about this state is wait for a
 *     retention clock or go and look at why a training record exists for a forgotten person.
 *   * `erasureIncomplete` — ADR-0014 §5.2's unfinished erasure. Deliberately its own state and not folded
 *     into `retained`: nothing is being KEPT about this person, the erasure simply has not finished, and
 *     the remedy is on the TENANT's own user-administration page (the retry), not here. An operator told
 *     "data has to be kept" about such a person would go looking for a retention obligation that does not
 *     exist.
 */
export function rowState(person: {
  purgeable: boolean;
  erasureComplete: boolean;
  retained: readonly Retained[];
}): "purge" | "retained" | "erasureIncomplete" {
  if (person.purgeable) return "purge";
  // Asked before `retained`, because an unfinished erasure can also have retained data and the remedy for
  // the erasure is the one that comes first: there is nothing to decide about the retention until the
  // erasure that was supposed to run has run.
  return person.erasureComplete ? "retained" : "erasureIncomplete";
}

/**
 * The i18n key for a retained class's name. A kind this build does not know gets its own sentence rather
 * than the raw wire value: a server that grew a class is telling the operator the row is held by something
 * this console cannot name, which is still a true and useful thing to say.
 */
export function retainedKindKey(kind: string): string {
  switch (kind) {
    case "employment_core":
      return "tenants.forgotten.kind.employment_core";
    case "identity_number":
      return "tenants.forgotten.kind.identity_number";
    case "work_permit":
      return "tenants.forgotten.kind.work_permit";
    case "absence_record":
      return "tenants.forgotten.kind.absence_record";
    case "training_record":
      return "tenants.forgotten.kind.training_record";
    case "certificate":
      return "tenants.forgotten.kind.certificate";
    case "person_link":
      return "tenants.forgotten.kind.person_link";
    case KIND_SHARED_IDENTITY:
      return "tenants.forgotten.kind.shared_identity";
    default:
      return "tenants.forgotten.kind.unknown";
  }
}

/**
 * Which SENTENCE a retained class gets, as opposed to which NAME. Three templates, because the three
 * states say different things and two of them are about a clock the third does not have:
 *
 *   * `retainedUntil` — ADR-0020 §10 stamped an end date, so the line names it and the ground.
 *   * `retainedNoClock` — §10 point 4's unstamped record: the obligation is real and its clock has not
 *     started, which is not a licence to delete either.
 *   * `retainedSharedIdentity` — the shared realm identity. It is NOT a retention hold, so neither clock
 *     sentence is true of it: there is no date that will ever arrive and no row count to report. An
 *     operator told "the retention period has not started" about an identity would wait for a deadline
 *     that does not exist, when the only thing that lifts this refusal is the OTHER tenant forgetting the
 *     same person.
 *
 * Here and not in the component for the reason in the header: it is a choice over a wire value.
 */
export function retainedLineKey(record: Retained): string {
  if (record.kind === KIND_SHARED_IDENTITY) return "tenants.forgotten.retainedSharedIdentity";
  return record.retainedUntil
    ? "tenants.forgotten.retainedUntil"
    : "tenants.forgotten.retainedNoClock";
}

/**
 * Which sentence a FAILED deletion gets, by the problem's `error` code and its status.
 *
 * Every arm is an ending in which NOTHING was deleted, and they are told apart by CODE and not by status —
 * four of them share the 409 and the remedy differs for each: wait for the retention obligation to end,
 * run the erasure's retry on the tenant's own page, run the forget first, or report a reference nobody has
 * classified. A 404 is its own sentence: the row is already gone, which for this operation means somebody
 * else deleted it rather than that this attempt failed.
 */
export function purgeErrorKey(status: number, problem: PurgeProblem): string {
  const code = problem && typeof problem.error === "string" ? problem.error : null;

  switch (code) {
    case ERROR_RETAINED_DATA_PRESENT:
      return "tenants.forgotten.errorRetained";
    case ERROR_ERASURE_INCOMPLETE:
      return "tenants.forgotten.errorErasureIncomplete";
    case ERROR_USER_NOT_FORGOTTEN:
      return "tenants.forgotten.errorNotForgotten";
    case ERROR_REFUSED_BY_DATABASE:
      return "tenants.forgotten.errorRefusedByDatabase";
    default:
      return status === 404 ? "tenants.forgotten.error404" : "tenants.forgotten.error";
  }
}

/**
 * The classes a refusal named, read off the problem's `retained` extension. Empty when the server sent
 * none, which is also what a 409 of any other code carries.
 *
 * Every entry is validated rather than trusted: `kind` has to be a non-empty string and `rows` has to
 * READ as a finite number, because this list is rendered as the reason an irreversible act was refused and
 * a half-shaped entry would render as "undefined" beside a count of "NaN". A problem whose extension is
 * not an array at all yields nothing, and the refusal's own sentence still stands on its own.
 */
export function retainedFromProblem(problem: PurgeProblem): Retained[] {
  const raw = problem?.retained;
  if (!Array.isArray(raw)) return [];

  const records: Retained[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    if (typeof record.kind !== "string" || record.kind === "") continue;
    if (typeof record.rows !== "number" && typeof record.rows !== "string") continue;
    if (record.rows === "" || !Number.isFinite(Number(record.rows))) continue;

    records.push({
      kind: record.kind,
      rows: record.rows,
      retainedUntil: typeof record.retainedUntil === "string" ? record.retainedUntil : null,
      ground: typeof record.ground === "string" ? record.ground : null,
    });
  }
  return records;
}

/**
 * The nav's line for this section: how many forgotten people the tenant has, and whether any of them can
 * be deleted. `undefined` is "the section has not been opened", which the nav must not turn into "none" —
 * that would be a positive claim about a read nobody has made.
 */
export function navStatusKey(
  people: readonly { purgeable: boolean }[] | undefined,
): { key: string; purgeable: number; total: number } {
  if (people === undefined) {
    return { key: "tenants.nav.unknown", purgeable: 0, total: 0 };
  }
  if (people.length === 0) {
    return { key: "tenants.forgotten.navNone", purgeable: 0, total: 0 };
  }
  const purgeable = people.filter((person) => person.purgeable).length;
  return purgeable === 0
    ? { key: "tenants.forgotten.navHeld", purgeable: 0, total: people.length }
    : { key: "tenants.forgotten.navPurgeable", purgeable, total: people.length };
}
